/**
 * Campaign template preview utilities.
 * Rendering reference: MessageTemplates.tsx TemplateCard (~661–702).
 */

export type TemplatePreviewButton = {
  type: string;
  text: string;
  url?: string;
  phone_number?: string;
};

export type TemplatePreviewData = {
  name?: string | null;
  bodyText?: string | null;
  headerType?: string | null;
  headerText?: string | null;
  headerImageUrl?: string | null;
  footerText?: string | null;
  buttons?: TemplatePreviewButton[];
};

export type TemplateMapEntry = {
  name?: string;
  body_text?: string | null;
  template?: string | null;
  message?: string | null;
  header_text?: string | null;
  header_type?: string | null;
  header_image_url?: string | null;
  footer_text?: string | null;
  buttons?: TemplatePreviewButton[];
};

export function normalizeTemplateKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '_');
}

export function formatTemplateName(name: string | null | undefined): string | null {
  if (!name) return null;
  if (/^\d{10,}$/.test(name)) return name;
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
}

export function cleanGupshupBody(
  body: string,
  footerText?: string | null
): string {
  let result = body.replace(/(\s*\|\s*\[.*?\])+$/g, '');

  const cleanFooter = footerText?.trim();

  if (cleanFooter) {
    const trimmedResult = result.trimEnd();

    // Gupshup puede agregar el footer al BODY separado por
    // espacios, salto de línea o "|".
    if (trimmedResult.endsWith(cleanFooter)) {
      const beforeFooter = trimmedResult.slice(
        0,
        -cleanFooter.length
      );

      const isSeparated =
        beforeFooter.length === 0 ||
        /[\s|]$/.test(beforeFooter);

      if (isSeparated) {
        result = beforeFooter
          .replace(/\s*\|\s*$/, '')
          .trimEnd();
      }
    }
  }

  return result.trim();
}

export type NormalizedTemplate = {
  id: string;
  meta_template_id?: string;
  name: string;
  displayName?: string | null;
  language: string;
  status: string;
  category: string;
  body: string;
  template: string;
  header_text?: string | null;
  header_type?: string | null;
  header_image_url?: string | null;
  footer_text?: string | null;
  buttons: TemplatePreviewButton[];
};

export function normalizeMetaTemplate(
  template: any,
  localTemplates: any[] = []
): NormalizedTemplate {
  const bodyComponent = template.components?.find(
    (component: any) => component.type === 'BODY'
  );
  const headerComponent = template.components?.find(
    (component: any) => component.type === 'HEADER'
  );
  const footerComponent = template.components?.find(
    (component: any) => component.type === 'FOOTER'
  );
  const buttonsComponent = template.components?.find(
    (component: any) => component.type === 'BUTTONS'
  );

  const footerText =
    footerComponent?.text ||
    template.footer_text ||
    null;

  const bodyText = cleanGupshupBody(
    bodyComponent?.text ||
      template.template ||
      template.body ||
      template.message ||
      '',
    footerText
  );

  const headerType =
    headerComponent?.format ||
    template.header_type ||
    null;

  const headerText =
    headerComponent?.format === 'TEXT'
      ? headerComponent.text
      : template.header_text || null;

  const headerImageUrl =
    headerComponent?.format === 'IMAGE'
      ? (
          headerComponent.example?.header_handle?.[0] ||
          headerComponent.example?.header_url?.[0] ||
          null
        )
      : template.header_image_url || null;

  const metaButtons: TemplatePreviewButton[] =
    buttonsComponent?.buttons?.map((button: any) => ({
      type: button.type,
      text: button.text,
      url: button.url,
      phone_number: button.phone_number,
    })) ||
    template.buttons ||
    [];

  const originalName = (template.name || '').toLowerCase();

  const localMatch = localTemplates.find(
    (localTemplate: any) =>
      normalizeTemplateKey(localTemplate.name || '') === originalName ||
      localTemplate.meta_template_name?.toLowerCase() === originalName
  );

  return {
    id: localMatch?._id || template.id || template._id,
    meta_template_id: template.id,
    name: template.name,
    displayName:
      template.displayName ||
      formatTemplateName(template.name),
    language: template.language || 'es_MX',
    status: template.status || 'APPROVED',
    category: template.category || 'UTILITY',
    body: bodyText,
    template: bodyText,
    header_text: localMatch?.header_text || headerText,
    header_type: localMatch?.header_type || headerType,
    header_image_url:
      localMatch?.header_image_url || headerImageUrl,
    footer_text:
      localMatch?.footer_text || footerText,
    buttons:
      localMatch?.buttons?.length
        ? localMatch.buttons
        : metaButtons,
  };
}

export function normalizeLocalTemplate(
  template: any,
  options: {
    defaultCategory?: string;
    defaultStatus?: string;
  } = {}
): NormalizedTemplate {
  const bodyText = cleanGupshupBody(
    template.template ||
      template.message ||
      template.body ||
      '',
    template.footer_text
  );

  return {
    id: template._id || template.id,
    name: template.name,
    displayName:
      template.displayName ||
      formatTemplateName(template.name),
    language: template.language || 'es_MX',
    status:
      template.status ||
      options.defaultStatus ||
      'APPROVED',
    category:
      template.category ||
      options.defaultCategory ||
      'UTILITY',
    body: bodyText,
    template: bodyText,
    header_text: template.header_text || null,
    header_type: template.header_type || null,
    header_image_url: template.header_image_url || null,
    footer_text: template.footer_text || null,
    buttons: template.buttons || [],
  };
}
export function parseLegacyMessageTemplate(raw: string): {
  bodyText: string;
  footerText?: string;
  buttons: TemplatePreviewButton[];
} {
  const parts = raw.split('|').map((p) => p.trim());
  const bodyParts: string[] = [];
  const buttons: TemplatePreviewButton[] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i > 0 && part.startsWith('[') && part.endsWith(']')) {
      const btnContent = part.substring(1, part.length - 1);
      const btnParts = btnContent.split(',');
      if (btnParts.length > 1) {
        buttons.push({ type: 'complex', text: btnParts[0].trim() });
      } else {
        buttons.push({ type: 'reply', text: btnContent.trim() });
      }
    } else {
      bodyParts.push(part);
    }
  }

  return {
    bodyText: bodyParts.join(' | ').trim(),
    buttons,
  };
}

function setMapEntry(
  map: Record<string, TemplateMapEntry>,
  name: string,
  entry: TemplateMapEntry
): void {
  map[name] = entry;
  map[normalizeTemplateKey(name)] = entry;
}

export function lookupTemplateInMap(
  map: Record<string, TemplateMapEntry>,
  templateName?: string | null
): TemplateMapEntry | undefined {
  if (!templateName) return undefined;
  return map[templateName] || map[normalizeTemplateKey(templateName)];
}

function entryToPreviewFields(entry: TemplateMapEntry): Partial<TemplatePreviewData> {
  const body =
    entry.body_text ?? entry.template ?? entry.message ?? null;
  return {
    bodyText: body,
    headerType: entry.header_type ?? null,
    headerText: entry.header_text ?? null,
    headerImageUrl: entry.header_image_url ?? null,
    footerText: entry.footer_text ?? null,
    buttons: entry.buttons?.length ? entry.buttons : [],
  };
}

/**
 * Build templates map from local DB + Meta list (mirrors MessageTemplates fetchTemplates enrichment).
 */
export function buildTemplatesMap(
  localTemplates: any[],
  metaTemplates: any[]
): Record<string, TemplateMapEntry> {
  const map: Record<string, TemplateMapEntry> = {};

  localTemplates.forEach((t: any) => {
    if (!t.name) return;
    const entry: TemplateMapEntry = {
      name: t.name,
      body_text: t.template || null,
      header_text: t.header_text || null,
      header_type: t.header_type || null,
      header_image_url: t.header_image_url || null,
      footer_text: t.footer_text || null,
      buttons: t.buttons || [],
    };
    setMapEntry(map, t.name, entry);
  });

  metaTemplates.forEach((t: any) => {
    if (!t.name) return;

    const headerComp = t.components?.find((c: any) => c.type === 'HEADER');
    const footerComp = t.components?.find((c: any) => c.type === 'FOOTER');
    const buttonsComp = t.components?.find((c: any) => c.type === 'BUTTONS');

    let headerType: string | null = null;
    let headerText: string | null = null;
    let headerImageUrl: string | null = null;

    if (headerComp) {
      headerType = headerComp.format || null;
      if (headerComp.format === 'TEXT') {
        headerText = headerComp.text || null;
      } else if (headerComp.format === 'IMAGE') {
        headerImageUrl =
          headerComp.example?.header_handle?.[0] ||
          headerComp.example?.header_url?.[0] ||
          null;
      }
    }

    const metaFooter = footerComp?.text || null;
    const metaButtons =
      buttonsComp?.buttons?.map((btn: any) => ({
        type: btn.type,
        text: btn.text,
        url: btn.url,
        phone_number: btn.phone_number,
      })) || [];

    const existing = lookupTemplateInMap(map, t.name) || {};

    const merged: TemplateMapEntry = {
      ...existing,
      name: existing.name || t.name,
      body_text: existing.body_text ?? existing.template ?? null,
      header_type: headerType || existing.header_type || null,
      header_text: headerText || existing.header_text || null,
      header_image_url: existing.header_image_url || headerImageUrl || null,
      footer_text: metaFooter || existing.footer_text || null,
      buttons: metaButtons.length ? metaButtons : existing.buttons || [],
    };

    setMapEntry(map, t.name, merged);
  });

  return map;
}

export type ResolveTemplatePreviewParams = {
  templateName?: string | null;
  templateId?: string;
  messageTemplateFallback?: string;
  templatesMap?: Record<string, TemplateMapEntry>;
  templateCache?: Record<string, TemplateMapEntry>;
  campaignFields?: Partial<TemplateMapEntry>;
};

export function resolveTemplatePreview(params: ResolveTemplatePreviewParams): TemplatePreviewData {
  const {
    templateName,
    templateId,
    messageTemplateFallback,
    templatesMap = {},
    templateCache = {},
    campaignFields = {},
  } = params;

  const displayName = formatTemplateName(templateName ?? campaignFields.name ?? null);

  let bodyText: string | null = null;
  let headerType: string | null = campaignFields.header_type ?? null;
  let headerText: string | null = campaignFields.header_text ?? null;
  let headerImageUrl: string | null = campaignFields.header_image_url ?? null;
  let footerText: string | null = campaignFields.footer_text ?? null;
  let buttons: TemplatePreviewButton[] = campaignFields.buttons?.length
    ? [...campaignFields.buttons]
    : [];

  const cached = templateId ? templateCache[String(templateId)] : undefined;
  const mapped = lookupTemplateInMap(templatesMap, templateName);

  const sources = [cached, mapped, campaignFields].filter(Boolean) as TemplateMapEntry[];

  for (const src of sources) {
    const fields = entryToPreviewFields(src);
    if (!bodyText && fields.bodyText) bodyText = fields.bodyText;
    if (!headerType && fields.headerType) headerType = fields.headerType;
    if (!headerText && fields.headerText) headerText = fields.headerText;
    if (!headerImageUrl && fields.headerImageUrl) headerImageUrl = fields.headerImageUrl;
    if (!footerText && fields.footerText) footerText = fields.footerText;
    if (!buttons.length && fields.buttons?.length) buttons = fields.buttons;
  }

  if (bodyText) {
    bodyText = cleanGupshupBody(bodyText, footerText);
  }

  if (!bodyText && messageTemplateFallback) {
    const parsed = parseLegacyMessageTemplate(messageTemplateFallback);
    bodyText = cleanGupshupBody(parsed.bodyText, footerText);
    if (!buttons.length && parsed.buttons.length) buttons = parsed.buttons;
    if (!footerText && parsed.footerText) footerText = parsed.footerText;
  }

  if (bodyText && !footerText && messageTemplateFallback) {
    const parsed = parseLegacyMessageTemplate(messageTemplateFallback);
    if (!buttons.length && parsed.buttons.length) buttons = parsed.buttons;
  }

  return {
    name: displayName,
    bodyText: bodyText || null,
    headerType,
    headerText,
    headerImageUrl,
    footerText,
    buttons,
  };
}

export function hasTemplatePreviewContent(preview: TemplatePreviewData): boolean {
  return !!(
    preview.name ||
    preview.bodyText ||
    preview.headerText ||
    preview.headerImageUrl ||
    (preview.buttons && preview.buttons.length > 0)
  );
}
